import React, { useState, useEffect } from 'react';
import { Settings, X, Save, Trash2, Edit } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { getCurrentUser } from '../utils/authService';
import styles from './CategoryFilterModal.module.css';
import Modal from 'react-modal';


const CategoryFilterModal = ({ 
  isOpen, 
  onClose, 
  categories, 
  visibleCategories, 
  onToggleCategory,
  onToggleAll,
  onViewChange
}) => {
  const { theme } = useTheme();
  const [viewName, setViewName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [savedViews, setSavedViews] = useState([]); // Initialize as empty array
  const [selectedView, setSelectedView] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchSavedViews();
    }
  }, [isOpen]);

  const fetchSavedViews = async () => {
    setIsLoading(true);
    try {
      const user = getCurrentUser();
      const response = await fetch(
        `/api/users/${user.user_id}/category-views`,
        {
          credentials: 'include' // ✅ Ensures authentication cookies are sent
        }
      );
      
      
      if (!response.ok) {
        throw new Error('Failed to fetch views');
      }

      const data = await response.json();
      // Ensure data is an array, if not use empty array
      setSavedViews(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching saved views:', error);
      setSavedViews([]); // Reset to empty array on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveView = async () => {
    if (!viewName.trim()) {
      alert('Please enter a view name');
      return;
    }

    try {
      const user = getCurrentUser();
      const viewData = {
        name: viewName,
        categories: JSON.stringify(visibleCategories),
        is_default: isDefault
      };

      const response = await fetch(
        `/api/users/${user.user_id}/category-views`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include' ,
          body: JSON.stringify(viewData)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save view');
      }

      await fetchSavedViews();
        if (onViewChange) {
          onViewChange(); 
        }
        setViewName('');
        setIsDefault(false);
    } catch (error) {
      console.error('Error saving view:', error);
      alert('Failed to save view. Please try again.');
    }
  };

 const handleLoadView = (view) => {
  setSelectedView(view);
  try {
    const parsed = JSON.parse(view.categories);
    onToggleAll(false); // Clear modal preview
    parsed.forEach((id) => onToggleCategory(id)); // Populate modal preview sliders
  } catch (error) {
    console.error('Error loading view:', error);
  }
};


  const handleDeleteView = async (viewId, e) => {
    e.stopPropagation(); // Prevent triggering view selection
    if (!window.confirm('Are you sure you want to delete this view?')) return;

    try {
      const user = getCurrentUser();
      const response = await fetch(
        `/api/users/${user.user_id}/category-views/${viewId}`,
        {
          method: 'DELETE',
          credentials: 'include' // ✅ Ensures authentication cookies are sent
        }
      );
      

      if (!response.ok) {
        throw new Error('Failed to delete view');
      }

      await fetchSavedViews();
      if (onViewChange) {
        onViewChange();
      }
      if (selectedView?.id === viewId) {
        setSelectedView(null);
      }
    } catch (error) {
      console.error('Error deleting view:', error);
      alert('Failed to delete view. Please try again.');
    }
  };

  const handleUpdateView = async (viewId, e) => {
  e.stopPropagation(); // Prevent triggering view selection
  if (!selectedView || selectedView.id !== viewId) {
    alert('Please select a view first to update it');
    return;
  }

  try {
    const user = getCurrentUser();
    const viewData = {
      name: selectedView.name,
      categories: JSON.stringify(visibleCategories),
      is_default: selectedView.is_default
    };

    const response = await fetch(
      `/api/users/${user.user_id}/category-views/${viewId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(viewData)
      }
    );

    if (!response.ok) {
      throw new Error('Failed to update view');
    }

    await fetchSavedViews();
    if (onViewChange) {
      onViewChange();
    }
    alert('View updated successfully!');
  } catch (error) {
    console.error('Error updating view:', error);
    alert('Failed to update view. Please try again.');
  }
};

  const allVisible = (categories?.length || 0) === (visibleCategories?.length || 0);


  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={styles.modalContent}
      overlayClassName={styles.modalOverlay}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
    >
      {/* Header */}
      <div className={styles.modalHeader}>
        <div className={styles.headerTitle}>
          <Settings className="w-5 h-5" />
          <h2>Customize View</h2>
        </div>
        <button onClick={onClose} className={styles.closeButton}>
          <X className="w-5 h-5" />
        </button>
      </div>
  
      {/* Saved Views Section */}
      <div className={styles.savedViewsSection}>
        <h3>Saved Views</h3>
        <div className={styles.viewsList}>
          {isLoading ? (
            <div className={styles.loadingText}>Loading saved views...</div>
          ) : savedViews.length > 0 ? (
            savedViews.map(view => (
              <div 
  key={view.id} 
  onClick={() => handleLoadView(view)}
  className={`${styles.viewItem} ${selectedView?.id === view.id ? styles.selected : ''}`}
>
  <span className={styles.viewName}>
    {view.name} {view.is_default && '(Default)'}
  </span>
  <div className={styles.viewActions}>
    <button
      onClick={(e) => handleUpdateView(view.id, e)}
      className={`${styles.updateButton} ${selectedView?.id !== view.id ? styles.disabled : ''}`}
      disabled={selectedView?.id !== view.id}
    >
      <Save className="w-4 h-4" />
    </button>
    <button
      onClick={(e) => handleDeleteView(view.id, e)}
      className={styles.deleteButton}
    >
      <Trash2 className="w-4 h-4" />
    </button>
  </div>
</div>
            ))
          ) : (
            <div className={styles.noViews}>No saved views</div>
          )}
        </div>
      </div>
  
      {/* Save New View Section */}
      <div className={styles.saveViewSection}>
        <div className={styles.saveViewInput}>
          <input
            type="text"
            value={viewName}
            onChange={(e) => setViewName(e.target.value)}
            placeholder="Enter view name"
            className={styles.viewNameInput}
          />
          <button onClick={handleSaveView} className={styles.saveButton}>
            <Save className="w-4 h-4" />
            Save View
          </button>
        </div>
        <label className={styles.defaultCheckbox}>
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          Set as default view
        </label>
      </div>
  
      {/* Toggle All Button */}
      <div className={styles.toggleAllSection}>
        <button
          onClick={() => onToggleAll(!allVisible)}
          className={styles.toggleAllButton}
        >
          {allVisible ? 'Hide All' : 'Show All'}
        </button>
      </div>
  
      {/* Category List */}
      <div className={styles.categoryList}>
       {categories.filter(category => !category.is_virtual).map(category => {
          const isVisible = visibleCategories.includes(category.id);
          return (
            <div key={category.id} className={styles.categoryItem}>
              <span className={styles.categoryName}>{category.name}</span>
              <label className={styles.toggleSwitch}>
                <input
                  type="checkbox"
                  checked={isVisible}
                  onChange={() => onToggleCategory(category.id)}
                  className="sr-only peer"
                />
                <div className={styles.toggleSlider}></div>
              </label>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default CategoryFilterModal;